/**
 * The learned autocomplete described in the source concept.
 *
 * Three behaviours, all local, all built from what the driver has already
 * typed — no network call is needed to suggest something entered before:
 *
 *  - typing `Kluse` resolves to Michael Kluse, because the surname is an
 *    indexed token on a record that already holds the first name;
 *  - typing `Am` surfaces Ambacherstraße 39, because it is the most-used
 *    address starting with that prefix;
 *  - once Flughafen München is the usual drop-off for a given passenger, it is
 *    offered as the destination before anything is typed.
 *
 * Ranking is frequency first, recency second. A prefix hit on the start of a
 * name beats a hit in the middle, and a whole-token hit beats a partial one,
 * so the obvious candidate surfaces on the first or second keystroke rather
 * than after five.
 */

import type { Address, Passenger, Trip, Uuid } from '@/domain/types';

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Fold case and German diacritics so `Grunwald`, `Grünwald` and `GRUENWALD`
 * all match each other. A driver typing at a kerbside will not reach for the
 * umlaut key.
 */
export function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/** Split into searchable tokens, dropping punctuation and one-char noise. */
export function tokenise(value: string): string[] {
  return normalise(value)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 0);
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/** Half-life for the recency component, in days. */
const RECENCY_HALF_LIFE_DAYS = 45;

const MATCH_WHOLE_FIELD_PREFIX = 1000;
const MATCH_TOKEN_PREFIX = 600;
const MATCH_TOKEN_SUBSTRING = 200;
const MATCH_FIELD_SUBSTRING = 100;

/**
 * How well `query` matches `haystack`, ignoring popularity. Zero means no
 * match at all and the candidate is dropped.
 */
export function matchScore(query: string, haystack: string): number {
  if (query === '') return 1;
  const normalisedQuery = normalise(query);
  const normalisedHaystack = normalise(haystack);
  if (normalisedQuery === '' || normalisedHaystack === '') return 0;

  if (normalisedHaystack.startsWith(normalisedQuery)) {
    // A shorter field matched by the same prefix is the more specific answer.
    return MATCH_WHOLE_FIELD_PREFIX - Math.min(normalisedHaystack.length, 99);
  }

  const tokens = tokenise(haystack);
  for (const token of tokens) {
    if (token.startsWith(normalisedQuery)) {
      return MATCH_TOKEN_PREFIX - Math.min(token.length, 99);
    }
  }
  for (const token of tokens) {
    if (token.includes(normalisedQuery)) return MATCH_TOKEN_SUBSTRING;
  }
  if (normalisedHaystack.includes(normalisedQuery)) return MATCH_FIELD_SUBSTRING;

  return 0;
}

/** Exponential decay on days since last use, in the range (0, 1]. */
export function recencyWeight(lastUsedAt: string, now: number = Date.now()): number {
  const days = Math.max(0, (now - new Date(lastUsedAt).getTime()) / 86_400_000);
  return Math.pow(0.5, days / RECENCY_HALF_LIFE_DAYS);
}

/**
 * Popularity component. Logarithmic, so the tenth trip to the airport matters
 * far less than the second — otherwise one dominant destination would bury
 * everything else forever.
 */
export function frequencyWeight(useCount: number): number {
  return Math.log2(1 + Math.max(0, useCount));
}

export function combinedScore(
  match: number,
  useCount: number,
  lastUsedAt: string,
  now?: number,
): number {
  if (match === 0) return 0;
  return match + frequencyWeight(useCount) * 40 + recencyWeight(lastUsedAt, now) * 25;
}

// ---------------------------------------------------------------------------
// Passengers
// ---------------------------------------------------------------------------

export interface PassengerSuggestion {
  passenger: Passenger;
  score: number;
  /** Which field produced the hit, so the UI can highlight it. */
  matchedOn: 'lastName' | 'firstName' | 'company' | 'phone';
}

export function suggestPassengers(
  passengers: readonly Passenger[],
  query: string,
  limit = 8,
  now?: number,
): PassengerSuggestion[] {
  const results: PassengerSuggestion[] = [];

  for (const passenger of passengers) {
    const candidates: Array<[PassengerSuggestion['matchedOn'], string | undefined]> = [
      ['lastName', passenger.lastName],
      ['firstName', passenger.firstName],
      ['company', passenger.company],
      ['phone', passenger.phone],
    ];

    let best = 0;
    let bestField: PassengerSuggestion['matchedOn'] = 'lastName';
    for (const [field, value] of candidates) {
      if (!value) continue;
      // The surname is the field a driver types, so it wins ties.
      const weight = field === 'lastName' ? 1.15 : field === 'company' ? 1.05 : 1;
      const score = matchScore(query, value) * weight;
      if (score > best) {
        best = score;
        bestField = field;
      }
    }

    if (best === 0) continue;
    results.push({
      passenger,
      score: combinedScore(best, passenger.useCount, passenger.lastUsedAt, now),
      matchedOn: bestField,
    });
  }

  return results.sort((a, b) => b.score - a.score).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

export interface AddressSuggestion {
  address: Address;
  score: number;
  /**
   * Why it is being offered. `history` is a plain text match; `passenger` means
   * this passenger habitually starts or ends there.
   */
  reason: 'history' | 'passenger';
}

export function suggestAddresses(
  addresses: readonly Address[],
  query: string,
  limit = 8,
  now?: number,
): AddressSuggestion[] {
  const results: AddressSuggestion[] = [];

  for (const address of addresses) {
    const searchable = [address.label, address.street, address.city, address.postalCode]
      .filter(Boolean)
      .join(' ');
    const match = Math.max(matchScore(query, address.label), matchScore(query, searchable) * 0.9);
    if (match === 0) continue;
    results.push({
      address,
      score: combinedScore(match, address.useCount, address.lastUsedAt, now),
      reason: 'history',
    });
  }

  return results.sort((a, b) => b.score - a.score).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Destination anticipation
// ---------------------------------------------------------------------------

/**
 * Per-passenger histogram of pickup and drop-off addresses.
 *
 * Built once at unlock from the trip history and updated incrementally as
 * trips are logged, so the "where does this customer usually go" question is
 * answered from memory rather than by scanning the table on every keystroke.
 */
export class DestinationIndex {
  private readonly fromByPassenger = new Map<Uuid, Map<Uuid, number>>();
  private readonly toByPassenger = new Map<Uuid, Map<Uuid, number>>();
  private readonly lastSeen = new Map<string, string>();

  static build(trips: readonly Trip[]): DestinationIndex {
    const index = new DestinationIndex();
    for (const trip of trips) index.add(trip);
    return index;
  }

  add(trip: Trip): void {
    bump(this.fromByPassenger, trip.passengerId, trip.fromAddressId);
    bump(this.toByPassenger, trip.passengerId, trip.toAddressId);
    this.lastSeen.set(`${trip.passengerId}:${trip.fromAddressId}`, trip.startedAt);
    this.lastSeen.set(`${trip.passengerId}:${trip.toAddressId}`, trip.startedAt);
  }

  /** Address IDs this passenger is usually collected from, most likely first. */
  likelyOrigins(passengerId: Uuid, limit = 3): Uuid[] {
    return rank(this.fromByPassenger.get(passengerId), limit);
  }

  /** Address IDs this passenger usually travels to, most likely first. */
  likelyDestinations(passengerId: Uuid, limit = 3): Uuid[] {
    return rank(this.toByPassenger.get(passengerId), limit);
  }

  /**
   * The single most likely destination, but only when the history is strong
   * enough to be worth pre-filling a field the driver would have to correct.
   */
  confidentDestination(passengerId: Uuid): Uuid | undefined {
    const counts = this.toByPassenger.get(passengerId);
    if (!counts) return undefined;
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const top = ranked[0];
    if (!top) return undefined;
    const total = ranked.reduce((sum, [, count]) => sum + count, 0);
    // At least three prior trips and a clear majority. Below that, guessing
    // costs the driver a correction and is worse than an empty field.
    if (top[1] < 3 || top[1] / total < 0.6) return undefined;
    return top[0];
  }

  tripCountFor(passengerId: Uuid): number {
    const counts = this.toByPassenger.get(passengerId);
    if (!counts) return 0;
    return [...counts.values()].reduce((sum, count) => sum + count, 0);
  }
}

function bump(map: Map<Uuid, Map<Uuid, number>>, outer: Uuid, inner: Uuid): void {
  let counts = map.get(outer);
  if (!counts) {
    counts = new Map();
    map.set(outer, counts);
  }
  counts.set(inner, (counts.get(inner) ?? 0) + 1);
}

function rank(counts: Map<Uuid, number> | undefined, limit: number): Uuid[] {
  if (!counts) return [];
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([id]) => id);
}

/**
 * Merge history-ranked suggestions with this passenger's habitual addresses.
 *
 * The result comes out in two bands: addresses this passenger actually uses,
 * then everything else, each band ranked by the ordinary frequency-and-recency
 * score. Where a customer has been before beats where the address book as a
 * whole goes most often — Messe München may be the busiest destination in the
 * book, but for a passenger who only ever flies it is noise.
 *
 * Banding is not the same as pinning. `suggestAddresses` has already dropped
 * everything that does not match the typed text, so a habitual address only
 * reaches the top band while the driver is still typing something it matches.
 * Type a genuinely new address and the habitual candidates disappear on the
 * keystroke that rules them out, rather than sitting there being scrolled past.
 */
export function suggestAddressesForPassenger(
  addresses: readonly Address[],
  query: string,
  habitualIds: readonly Uuid[],
  limit = 8,
  now?: number,
): AddressSuggestion[] {
  const habitual = new Set(habitualIds);
  const base = suggestAddresses(addresses, query, limit * 3, now);

  const marked: AddressSuggestion[] = base.map((suggestion) =>
    habitual.has(suggestion.address.id)
      ? { ...suggestion, reason: 'passenger' as const }
      : suggestion,
  );

  // With an empty query, offer the habitual addresses even if they would not
  // have made the frequency cut across the whole address book.
  if (query.trim() === '') {
    const byId = new Map(addresses.map((address) => [address.id, address]));
    for (const id of habitualIds) {
      if (marked.some((s) => s.address.id === id)) continue;
      const address = byId.get(id);
      if (address) {
        marked.push({
          address,
          score: combinedScore(1, address.useCount, address.lastUsedAt, now),
          reason: 'passenger',
        });
      }
    }
  }

  return marked
    .sort((a, b) => {
      const band = bandOf(b) - bandOf(a);
      return band !== 0 ? band : b.score - a.score;
    })
    .slice(0, limit);
}

function bandOf(suggestion: AddressSuggestion): number {
  return suggestion.reason === 'passenger' ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Name parsing
// ---------------------------------------------------------------------------

export interface ParsedName {
  firstName: string;
  lastName: string;
}

/**
 * Split free text into first and last name.
 *
 * Handles `Kluse, Michael`, `Michael Kluse`, and the German nobiliary and
 * compound particles (`von`, `van der`, `zu`) that belong with the surname.
 */
export function parseName(input: string): ParsedName {
  const trimmed = input.trim().replace(/\s+/g, ' ');
  if (trimmed === '') return { firstName: '', lastName: '' };

  if (trimmed.includes(',')) {
    const [last = '', first = ''] = trimmed.split(',', 2);
    return { firstName: first.trim(), lastName: last.trim() };
  }

  const parts = trimmed.split(' ');
  if (parts.length === 1) return { firstName: '', lastName: parts[0]! };

  const particles = new Set(['von', 'van', 'de', 'der', 'den', 'du', 'zu', 'zum', 'la', 'le']);
  let splitAt = parts.length - 1;
  for (let i = parts.length - 2; i >= 1; i -= 1) {
    if (particles.has(parts[i]!.toLowerCase())) splitAt = i;
    else break;
  }

  return {
    firstName: parts.slice(0, splitAt).join(' '),
    lastName: parts.slice(splitAt).join(' '),
  };
}

/** Display form: `Michael Kluse`, or the company when there is no person. */
export function passengerDisplayName(passenger: Passenger): string {
  const person = [passenger.firstName, passenger.lastName].filter(Boolean).join(' ');
  if (person && passenger.company) return `${person} (${passenger.company})`;
  return person || passenger.company || '—';
}
